import React from "react";
import { Link } from "react-router-dom";
import { home, countries } from "@/content/site";
import { Eyebrow, SectionHeading, SectionBody, Section } from "@/components/Section";
import { ButtonPrimary, ButtonSecondary } from "@/components/Buttons";
import Logo from "@/components/Logo";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-12 pt-16 md:pt-24 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 fade-up">
            <div className="flex items-center gap-3 mb-10">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--ochre)]">
                01 — {home.hero.eyebrow}
              </span>
              <div className="h-px bg-[var(--ink)] flex-1 max-w-[200px] rule-draw" />
            </div>
            <h1
              className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[6.25rem] leading-[0.95] tracking-tight text-[var(--ink)] mb-10"
              data-testid="home-hero-headline"
            >
              {home.hero.headline}
            </h1>
            <p className="text-lg md:text-xl leading-snug text-[var(--ink)] max-w-2xl mb-6">
              {home.hero.subheadline}
            </p>
            <p className="text-base leading-relaxed text-[var(--muted)] max-w-2xl mb-10">
              {home.hero.support}
            </p>
            <div className="flex flex-wrap gap-4" data-testid="home-hero-ctas">
              <ButtonPrimary to={home.hero.ctas[0].to} data-testid="cta-explore-solutions">
                {home.hero.ctas[0].label}
              </ButtonPrimary>
              <ButtonSecondary to={home.hero.ctas[1].to} data-testid="cta-request-call">
                {home.hero.ctas[1].label}
              </ButtonSecondary>
            </div>
          </div>

          {/* Right-side meta panel */}
          <aside className="lg:col-span-4 lg:border-l border-[var(--rule)] lg:pl-10 flex flex-col justify-between gap-10">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--muted)] mb-4">
                Coordination Index
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ["07", "Platform layers"],
                  ["12", "Service pathways"],
                  ["03", "Target regions"],
                  ["08", "Ecosystem partners"],
                ].map(([n, l]) => (
                  <div key={l} className="border-t border-[var(--ink)] pt-3">
                    <div className="font-display text-3xl md:text-4xl text-[var(--ink)] leading-none">
                      {n}
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)] mt-2">
                      {l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-[var(--ink)] pt-5">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--muted)] mb-3">
                Operating Framework
              </div>
              <div className="flex items-center gap-3 text-sm font-display">
                {countries.map((c, i) => (
                  <React.Fragment key={c}>
                    <span>{c}</span>
                    {i < countries.length - 1 && (
                      <span className="text-[var(--ochre)]">·</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* AUDIENCE CARDS */}
      <Section index="02" eyebrow="Audience Pathways">
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-[var(--ink)]">
          {home.audienceCards.map((c, i) => (
            <Link
              to={c.to}
              key={c.title}
              className={
                "group p-8 md:p-10 border-[var(--rule)] hover:bg-[var(--bone-2)] transition-colors duration-300 " +
                (i > 0 ? "md:border-l" : "")
              }
              data-testid={`audience-card-${i}`}
            >
              <div className="flex items-start justify-between mb-8">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--ochre)]">
                  0{i + 1}
                </div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="text-[var(--ink)] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                >
                  <path d="M7 17L17 7M17 7H9M17 7v8" />
                </svg>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[var(--ink)] mb-5 leading-tight">
                {c.title}
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-[var(--muted)]">
                {c.body}
              </p>
            </Link>
          ))}
        </div>
      </Section>

      {/* WHAT IS HUBINALL */}
      <Section index="03" eyebrow="What is Hubinall">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <SectionHeading>{home.whatIsHubinall.heading}</SectionHeading>
          </div>
          <div className="lg:col-span-6 lg:pl-8 lg:border-l border-[var(--rule)]">
            <SectionBody>{home.whatIsHubinall.body}</SectionBody>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-collapsed mt-8">
          {home.whatIsHubinall.blocks.map((b, i) => (
            <div key={b.title} className="p-8 bg-[var(--bone)] hover:bg-[var(--bone-2)] transition-colors duration-300 group">
              <div className="flex items-baseline gap-4 mb-5">
                <span className="font-mono text-xs tracking-[0.2em] text-[var(--ochre)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-px flex-1 bg-[var(--rule)] group-hover:bg-[var(--ochre)] transition-colors" />
              </div>
              <h3 className="font-display text-lg md:text-xl text-[var(--ink)] mb-3 leading-tight">
                {b.title}
              </h3>
              <p className="text-sm leading-relaxed text-[var(--muted)]">{b.body}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 mt-14">
          <ButtonPrimary to={home.whatIsHubinall.ctas[0].to}>
            {home.whatIsHubinall.ctas[0].label}
          </ButtonPrimary>
          <ButtonSecondary to={home.whatIsHubinall.ctas[1].to}>
            {home.whatIsHubinall.ctas[1].label}
          </ButtonSecondary>
        </div>
      </Section>

      {/* WHO WE SERVE */}
      <Section index="04" eyebrow="Who we serve">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <SectionHeading>{home.whoWeServe.heading}</SectionHeading>
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l border-[var(--rule)]">
            <SectionBody>{home.whoWeServe.body}</SectionBody>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--rule)]">
          {home.whoWeServe.blocks.map((b, i) => (
            <div key={b.title} className="bg-[var(--bone)] p-8 md:p-10">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--ochre)]">
                  0{i + 1}
                </span>
                <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--muted)]">
                  Audience
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl mb-4 leading-tight">{b.title}</h3>
              <p className="text-sm md:text-base leading-relaxed text-[var(--muted)]">{b.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* REGIONS */}
      <Section index="05" eyebrow="Operating environments" className="bg-[var(--bone-2)]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <SectionHeading>{home.regions.heading}</SectionHeading>
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l border-[var(--rule-strong)]">
            <SectionBody>{home.regions.body}</SectionBody>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--ink)]">
          {home.regions.blocks.map((b, i) => (
            <div key={b.title} className="bg-[var(--bone)] p-8 md:p-10 relative group">
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--ochre)]">
                  Region / 0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl mb-5 leading-tight">{b.title}</h3>
              <div className="h-px bg-[var(--ink)] w-16 mb-5 group-hover:w-32 transition-all duration-500" />
              <p className="text-sm md:text-base leading-relaxed text-[var(--muted)]">{b.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PARTNERS PREVIEW */}
      <Section index="06" eyebrow="Ecosystem">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <SectionHeading>{home.partnersPreview.heading}</SectionHeading>
          </div>
          <div className="lg:col-span-5 lg:pl-8 lg:border-l border-[var(--rule)]">
            <SectionBody>{home.partnersPreview.body}</SectionBody>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-b border-[var(--ink)]">
          <div className="py-10 md:py-12 md:pr-10 md:border-r border-[var(--rule)]">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[var(--ochre)] mb-6">
              Core Partners
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {home.partnersPreview.core.map((p) => (
                <span key={p} className="font-display text-xl md:text-2xl text-[var(--ink)]">
                  {p}
                </span>
              ))}
            </div>
          </div>
          <div className="py-10 md:py-12 md:pl-10">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[var(--ochre)] mb-6">
              Strategic Ecosystem Partners
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {home.partnersPreview.strategic.map((p) => (
                <span key={p} className="font-display text-xl md:text-2xl text-[var(--ink)]">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-10">
          <ButtonSecondary to="/partners">View Full Ecosystem</ButtonSecondary>
        </div>
      </Section>

      {/* FINAL CTA BAND */}
      <section className="bg-[var(--ink)] text-[var(--bone)] border-t border-[var(--ink-2)]" data-testid="home-final-cta">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--ochre)]">
                07 — Next step
              </span>
              <div className="h-px bg-[var(--bone)]/30 w-24" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-6">
              {home.finalCta.heading}
            </h2>
            <p className="text-base md:text-lg text-[var(--bone)]/75 leading-relaxed max-w-2xl">
              {home.finalCta.body}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link
              to={home.finalCta.ctas[0].to}
              className="group flex items-center justify-between bg-[var(--ochre)] text-[var(--bone)] px-6 py-5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors"
              data-testid="cta-start-setup"
            >
              {home.finalCta.ctas[0].label}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
            <Link
              to={home.finalCta.ctas[1].to}
              className="group flex items-center justify-between border border-[var(--bone)]/40 text-[var(--bone)] px-6 py-5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[var(--bone)] hover:text-[var(--ink)] transition-colors"
              data-testid="cta-explore-solutions-bottom"
            >
              {home.finalCta.ctas[1].label}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Floating theme toggle */}
      <div className="fixed bottom-6 left-6 z-50 flex items-center gap-2 bg-[var(--bone)] border border-[var(--ink)] px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="theme-toggle">
        <span className="text-[var(--ochre)]">●</span>
        <span className="text-[var(--muted)]">Theme A · Architect</span>
        <Link to="/" className="text-[var(--ochre)] hover:underline ml-3">Nova →</Link>
        <Link to="/theme-b" className="text-[var(--ochre)] hover:underline">B</Link>
      </div>
    </div>
  );
}
